import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GetSubscriptionsCall } from "../../../redux/api/subscription.api";
import { GetUserByIdCall } from "../../../redux/api/auth";
import { useExchangeRates } from "hooks/useExchngeRates";

function SubscriptionDashboard() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalWithTransactions, setTotalWithTransactions] = useState(0);

  // Performance metrics
  const [performance, setPerformance] = useState({});
  const [warnings, setWarnings] = useState([]);

  // ✅ User country lookup map
  const [userCountryMap, setUserCountryMap] = useState({});
  const [loadingCountries, setLoadingCountries] = useState(true);

  // ✅ Get exchange rates hook
  const { rates, loading: ratesLoading, error: ratesError } = useExchangeRates();

  // ✅ Fetch countries only for user IDs on the current page (one batch of up to 8 calls)
  const fetchCountriesForCurrentPage = useCallback(async (subscriptionList) => {
    const list = subscriptionList || [];
    const userIds = [...new Set(list.map((s) => s.user_id ?? s.id ?? s.userId).filter(Boolean))];
    if (userIds.length === 0) {
      setUserCountryMap({});
      setLoadingCountries(false);
      return;
    }
    setLoadingCountries(true);
    try {
      const results = await Promise.all(userIds.map((id) => GetUserByIdCall(id)));
      const countryMap = {};
      userIds.forEach((id, i) => {
        const response = results[i];
        if (!id) return;
        const isError = !response || typeof response !== "object" || (response.status && response.status >= 400);
        if (isError) {
          countryMap[id] = null;
          return;
        }
        const user = response?.data ?? response?.user ?? response;
        const country = user?.country ?? response?.country ?? null;
        countryMap[id] = country;
      });
      setUserCountryMap(countryMap);
    } catch (err) {
      console.error("Failed to fetch user countries:", err);
      setUserCountryMap({});
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const fetchSubscriptions = useCallback(async (pageNumber = 0, pageSize = 8, status = null) => {
    setLoading(true);
    setError("");
    setWarnings([]);

    try {
      const offset = pageNumber * pageSize;

      const response = await GetSubscriptionsCall({
        limit: pageSize,
        offset: offset,
        status: status && status !== "ALL" ? status : undefined,
        concurrency: 10,
      });

      const data = response.data || [];
      setSubscriptions(data);
      setTotalUsers(response.total_users || 0);
      setTotalWithTransactions(response.total_with_transactions || 0);
      setPerformance(response.performance || {});
      setWarnings(response.warnings || []);
      setLastUpdated(new Date());

      // Fetch countries only for users on this page (up to pageSize calls)
      fetchCountriesForCurrentPage(data);
    } catch (err) {
      setError(err?.message || "Failed to load subscription data");
      setSubscriptions([]);
      setLoadingCountries(false);
    } finally {
      setLoading(false);
    }
  }, [fetchCountriesForCurrentPage]);

  useEffect(() => {
    fetchSubscriptions(page, rowsPerPage, statusFilter);
  }, [page, rowsPerPage, statusFilter, fetchSubscriptions]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchSubscriptions(page, rowsPerPage, statusFilter);
  };

  // Helper function to get country flag emoji
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;

    const code = countryCode.toUpperCase();

    if (code.length !== 2) return null;

    const codePoints = [...code].map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  // ✅ Convert price to USD
  const convertToUSD = useCallback(
    (price, currency) => {
      if (!price || price === "N/A" || !currency) return null;

      const currencyUpper = currency.toUpperCase();

      // If already USD, return the price
      if (currencyUpper === "USD") return Number(price);

      // Get exchange rate
      const rate = rates[currencyUpper];

      if (!rate) {
        console.warn(`Exchange rate not found for ${currencyUpper}`);
        return null;
      }

      return Number(price) * rate;
    },
    [rates]
  );

  // ✅ Format price with USD conversion
  const formatPrice = useCallback(
    (price, currency) => {
      if (price === "N/A" || !price) return "N/A";

      const numPrice = Number(price);
      const currencyUpper = currency?.toUpperCase() || "USD";

      // If already USD
      if (currencyUpper === "USD") {
        return `$${numPrice.toFixed(2)}`;
      }

      // Convert to USD
      const usdPrice = convertToUSD(price, currency);

      if (usdPrice === null) {
        // Fallback: show original price only
        return `${currencyUpper} ${numPrice.toFixed(2)}`;
      }

      // Format: $8.85 (CAD 11.99)
      return `$${usdPrice.toFixed(2)} (${currencyUpper} ${numPrice.toFixed(2)})`;
    },
    [convertToUSD]
  );

  // Helper function to extract subscription details
  const getSubscriptionDetails = (sub) => {
    const subscriptions = sub.subscriptions || {};
    const subscriptionKey = Object.keys(subscriptions)[0];

    if (!subscriptionKey) {
      return {
        packageName: "N/A",
        price: "N/A",
        currency: "",
        expiresDate: null,
      };
    }

    const subscription = subscriptions[subscriptionKey];
    const entitlements = sub.entitlements || {};
    const entitlementKey = Object.keys(entitlements)[0];

    return {
      packageName: entitlementKey || subscription.display_name || "N/A",
      price: subscription.price?.amount || "N/A",
      currency: subscription.price?.currency || "",
      expiresDate: subscription.expires_date || null,
      store: subscription.store || "N/A",
    };
  };

  // Client-side search (only searches current page)
  const filteredSubscriptions = useMemo(() => {
    if (!searchTerm) return subscriptions;

    const term = searchTerm.toLowerCase();
    return subscriptions.filter((sub) => {
      return [sub.email, sub.name]
        .filter(Boolean)
        .some((field) => field.toString().toLowerCase().includes(term));
    });
  }, [subscriptions, searchTerm]);

  // Stats from backend response
  const stats = useMemo(() => {
    const currentPageTotal = subscriptions.length;
    const subscribed = subscriptions.filter((s) => s.status === "SUBSCRIBED").length;
    const renewed = subscriptions.filter((s) => s.status === "RENEWED").length;
    const unsubscribed = subscriptions.filter((s) => s.status === "UNSUBSCRIBED").length;

    return {
      total: totalUsers,
      currentPageTotal,
      subscribed,
      renewed,
      unsubscribed,
      withTransactions: totalWithTransactions,
    };
  }, [subscriptions, totalUsers, totalWithTransactions]);

  const headerCellSx = {
    px: 2,
    py: 1.5,
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
  };

  const bodyCellSx = {
    py: 1.5,
    px: 2,
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusStyles = {
    SUBSCRIBED: { color: "success", label: "Subscribed" },
    RENEWED: { color: "info", label: "Renewed" },
    UNSUBSCRIBED: { color: "default", label: "Unsubscribed" },
  };

  // Platform icon component
  const PlatformIcon = ({ platform }) => {
    if (!platform) {
      return <Chip size="small" label="N/A" variant="outlined" color="default" />;
    }

    if (platform.toLowerCase() === "ios") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "#000" }}>
          <AppleIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Avatar>
      );
    }

    if (platform.toLowerCase() === "android") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "#3DDC84" }}>
          <AndroidIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Avatar>
      );
    }

    return <Chip size="small" label={platform.toUpperCase()} variant="outlined" color="default" />;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <div>
            <MDTypography variant="h4" fontWeight="medium">
              Subscription Dashboard
            </MDTypography>
            <MDTypography variant="button" color="text">
              Monitor and manage RevenueCat subscriptions
            </MDTypography>
            {lastUpdated && (
              <MDTypography variant="caption" color="text" display="block">
                Last updated: {formatDate(lastUpdated)}
              </MDTypography>
            )}
            {performance?.execution_time_ms && (
              <MDTypography variant="caption" color="text" display="block">
                Load time: {(performance.execution_time_ms / 1000).toFixed(2)}s
                {performance.avg_time_per_user && ` (${performance.avg_time_per_user}ms per user)`}
              </MDTypography>
            )}
          </div>
          <MDButton color="info" variant="contained" onClick={handleRefresh} disabled={loading}>
            <Icon sx={{ mr: 1 }}>{loading ? "hourglass_top" : "refresh"}</Icon>
            {loading ? "Refreshing..." : "Refresh"}
          </MDButton>
        </MDBox>

        {/* Loading Countries Indicator */}
        {loadingCountries && (
          <MDBox mb={3}>
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Loading user countries...
            </Alert>
          </MDBox>
        )}

        {/* Exchange Rates Error */}
        {ratesError && (
          <MDBox mb={3}>
            <Alert severity="warning">
              Exchange rates unavailable. Prices will show original currency only.
            </Alert>
          </MDBox>
        )}

        {/* Warning Messages */}
        {warnings.length > 0 && (
          <MDBox mb={3}>
            <Alert severity="warning">
              {warnings.length} user(s) failed to load from RevenueCat
            </Alert>
          </MDBox>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="Total Users" value={stats.total} icon="group" color="info" />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Subscribed (Current Page)"
              value={stats.subscribed}
              icon="trending_up"
              color="success"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Renewed (Current Page)"
              value={stats.renewed}
              icon="autorenew"
              color="info"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Unsubscribed (Current Page)"
              value={stats.unsubscribed}
              icon="trending_down"
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Total With Transactions"
              value={stats.withTransactions}
              icon="receipt_long"
              color="primary"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              title="Showing"
              value={stats.currentPageTotal}
              icon="visibility"
              color="dark"
            />
          </Grid>
        </Grid>

        {/* Search and Filter */}
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center" marginTop={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by email or name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ "& .MuiInputBase-root": { height: 40 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  select
                  label="Filter by Status"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  sx={{ "& .MuiInputBase-root": { height: 40 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>filter_alt</Icon>
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="SUBSCRIBED">Subscribed</MenuItem>
                  <MenuItem value="RENEWED">Renewed</MenuItem>
                  <MenuItem value="UNSUBSCRIBED">Unsubscribed</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3} display="flex" alignItems="center">
                <MDTypography
                  variant="button"
                  color="text"
                  display="block"
                  align="right"
                  width="100%"
                >
                  {searchTerm
                    ? `Showing ${filteredSubscriptions.length} of ${stats.currentPageTotal}`
                    : `Page ${page + 1} of ${Math.ceil(stats.total / rowsPerPage)}`}
                </MDTypography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <MDBox mt={2}>
            <Alert severity="error" action={<MDButton onClick={handleRefresh}>Retry</MDButton>}>
              {error}
            </Alert>
          </MDBox>
        )}

        {/* Subscriptions Table */}
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title="Subscriptions"
            subheader={`Latest RevenueCat snapshot${
              lastUpdated ? ` · ${formatDate(lastUpdated)}` : ""
            }`}
          />
          <Divider />

          {loading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={28} />
              <MDTypography variant="button" color="text" ml={2}>
                Loading subscriptions...
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 900 }}>
                  {/* <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx} align="left">
                        User Information
                      </TableCell>
                      <TableCell sx={headerCellSx} align="center">
                        Platform
                      </TableCell>
                      <TableCell sx={headerCellSx} align="left">
                        Status
                      </TableCell>
                      <TableCell sx={headerCellSx} align="left">
                        Package
                      </TableCell>
                      <TableCell sx={headerCellSx} align="left">
                        Price
                      </TableCell>
                      <TableCell sx={headerCellSx} align="left">
                        Expires Date
                      </TableCell>
                      <TableCell sx={headerCellSx} align="left">
                        Last Seen
                      </TableCell>
                    </TableRow>
                  </TableHead> */}

                  <TableBody>
                    {filteredSubscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <MDTypography variant="button" color="text">
                            {searchTerm
                              ? "No subscriptions match your search"
                              : statusFilter !== "ALL"
                              ? "No subscriptions match your filter"
                              : "No subscription data available"}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSubscriptions.map((sub) => {
                        const details = getSubscriptionDetails(sub);
                        const userId = sub.user_id ?? sub.id ?? sub.userId;
                        const countryCode = userCountryMap[userId];
                        const countryFlag = getCountryFlag(countryCode);

                        return (
                          <TableRow key={userId || sub.email || sub.name} hover>
                            {/* User Information with Country Flag */}
                            <TableCell sx={{ ...bodyCellSx, minWidth: 240 }} align="left">
                              <Box display="flex" alignItems="center" gap={1}>
                                {countryFlag ? (
                                  <Box
                                    component="span"
                                    sx={{ fontSize: "1.5rem", lineHeight: 1 }}
                                    title={countryCode}
                                  >
                                    {countryFlag}
                                  </Box>
                                ) : (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "1rem",
                                      lineHeight: 1,
                                      color: "text.secondary",
                                    }}
                                  >
                                    🌍
                                  </Box>
                                )}

                                <Box>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    display="block"
                                  >
                                    {sub.name || "Unknown User"}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" display="block">
                                    {sub.email || "No email"}
                                  </MDTypography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Platform with Icon */}
                            <TableCell sx={bodyCellSx} align="center">
                              <Box display="flex" justifyContent="center">
                                <PlatformIcon platform={sub.platform} />
                              </Box>
                            </TableCell>

                            {/* Status */}
                            <TableCell sx={bodyCellSx} align="left">
                              <Chip
                                size="small"
                                label={statusStyles[sub.status]?.label || sub.status || "Unknown"}
                                color={statusStyles[sub.status]?.color || "default"}
                                variant="outlined"
                              />
                            </TableCell>

                            {/* Package Name */}
                            <TableCell sx={{ ...bodyCellSx, minWidth: 180 }} align="left">
                              <MDTypography variant="caption" fontWeight="medium">
                                {details.packageName}
                              </MDTypography>
                            </TableCell>

                            {/* Price - ✅ Updated */}
                            <TableCell sx={{ ...bodyCellSx, minWidth: 140 }} align="left">
                              <MDTypography variant="caption" fontWeight="medium" color="success">
                                {formatPrice(details.price, details.currency)}
                              </MDTypography>
                            </TableCell>

                            {/* Expires Date */}
                            <TableCell sx={bodyCellSx} align="left">
                              <MDTypography variant="caption" color="text">
                                {formatDate(details.expiresDate)}
                              </MDTypography>
                            </TableCell>

                            {/* Last Seen */}
                            <TableCell sx={bodyCellSx} align="left">
                              <MDTypography variant="caption" color="text">
                                {formatDate(sub.last_seen)}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={totalUsers}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[8, 10, 25, 50, 100, 200]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

const StatCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <div>
          <MDTypography variant="button" color="text">
            {title}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="medium">
            {value}
          </MDTypography>
        </div>
        <MDBox
          display="grid"
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            width: 60,
            height: 60,
            borderRadius: theme.borders.borderRadius.md,
            backgroundColor: theme.palette[color]?.light || theme.palette.info.light,
            color: theme.palette[color]?.main || theme.palette.info.main,
          })}
        >
          <Icon>{icon}</Icon>
        </MDBox>
      </MDBox>
    </CardContent>
  </Card>
);

export default SubscriptionDashboard;
